import { Controller } from "@hotwired/stimulus"
import { GridStack } from "gridstack"

// Wraps GridStack for the custom dashboard (see custom-dashboard-widgets
// design.md, Decision 5). NOT an extension of drag_controller.js: that one
// wraps Sortable.js for 1D reordering with immediate per-drop persistence,
// which is the wrong shape for a 2D grid with explicit batch Save/Cancel.
//
// Edit session model: view mode keeps the grid static (no drag, no resize —
// resizing is disabled everywhere per the "fixed size per widget type"
// product decision). Entering edit mode makes it interactive; nothing is
// persisted until Save reads GridStack's own in-memory layout and PATCHes
// it in one request. Cancel just reloads the page — the simplest way to
// "re-initialize from the last-saved layout" without hand-rolling undo logic
// for every possible in-session change (move/add/remove).
export default class extends Controller {
  static targets = ["grid", "editButton", "editToolbar", "addWidgetButton", "addWidgetMenu", "template", "editOnly", "saveErrorTemplate"]
  static values = { saveUrl: String, column: Number, cellHeight: Number }

  connect() {
    this.grid = GridStack.init({
      column: this.columnValue,
      cellHeight: this.cellHeightValue,
      margin: 8,
      float: false,
      staticGrid: true,
      handle: ".widget-header",
      disableResize: true
    }, this.gridTarget)

    this.closeMenusOnOutsideClick = this.closeMenusOnOutsideClick.bind(this)
    document.addEventListener("click", this.closeMenusOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.closeMenusOnOutsideClick)
    if (this.grid) {
      this.grid.destroy(false)
      this.grid = null
    }
  }

  enterEdit() {
    this.grid.setStatic(false)
    this.editButtonTarget.classList.add("hidden")
    this.editToolbarTarget.classList.remove("hidden")
    this.editToolbarTarget.classList.add("flex")
    this.editOnlyTargets.forEach((el) => el.classList.remove("hidden"))
  }

  exitEdit() {
    this.grid.setStatic(true)
    this.editButtonTarget.classList.remove("hidden")
    this.editToolbarTarget.classList.add("hidden")
    this.editToolbarTarget.classList.remove("flex")
    this.editOnlyTargets.forEach((el) => el.classList.add("hidden"))
    this.closeAllMenus()
  }

  cancel() {
    window.location.reload()
  }

  async save() {
    const widgets = this.grid.save(false).map((item) => ({ key: item.id, x: item.x, y: item.y }))
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    let response
    try {
      response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || ""
        },
        credentials: "same-origin",
        body: JSON.stringify({ widgets })
      })
    } catch (error) {
      this.showSaveError()
      return
    }

    if (response.ok) {
      this.exitEdit()
    } else {
      this.showSaveError()
    }
  }

  showSaveError() {
    if (!this.hasSaveErrorTemplateTarget) return

    const notification = this.saveErrorTemplateTarget.content.firstElementChild.cloneNode(true)
    document.body.appendChild(notification)
  }

  toggleAddWidgetMenu(event) {
    event.stopPropagation()
    const wasHidden = this.addWidgetMenuTarget.classList.contains("hidden")
    this.closeAllMenus()
    if (wasHidden) this.addWidgetMenuTarget.classList.remove("hidden")
  }

  toggleKebab(event) {
    event.stopPropagation()
    const menu = event.currentTarget.nextElementSibling
    const wasHidden = menu.classList.contains("hidden")
    this.closeAllMenus()
    if (wasHidden) menu.classList.remove("hidden")
  }

  closeMenusOnOutsideClick() {
    this.closeAllMenus()
  }

  closeAllMenus() {
    if (this.hasAddWidgetMenuTarget) this.addWidgetMenuTarget.classList.add("hidden")
    this.element.querySelectorAll('[data-dashboard-grid-target="kebabMenu"]').forEach((menu) => {
      menu.classList.add("hidden")
    })
  }

  addWidget(event) {
    const key = event.params.widgetKey
    const template = this.templateTargets.find((el) => el.dataset.widgetKey === key)
    if (!template) return

    const node = template.content.firstElementChild.cloneNode(true)
    this.grid.addWidget(node)

    template.remove()
    event.currentTarget.remove()
    this.closeAllMenus()
  }

  removeWidget(event) {
    const item = event.currentTarget.closest(".grid-stack-item")
    if (!item) return

    this.restoreToAddWidgetMenu(item)
    this.grid.removeWidget(item)
    this.closeAllMenus()
  }

  // Puts a just-removed widget back into the "+ Add widget" list (cloned
  // from the DOM node being removed, stripped of its saved x/y so GridStack
  // auto-places it wherever it's re-added) — without this, a widget removed
  // and re-added within the same edit session would simply vanish, since
  // the available-widgets list is otherwise computed once at page load.
  restoreToAddWidgetMenu(item) {
    if (!this.hasAddWidgetMenuTarget) return

    const key = item.dataset.widgetKey
    const title = item.querySelector(".widget-header span")?.textContent || key

    const clone = item.cloneNode(true)
    clone.removeAttribute("gs-x")
    clone.removeAttribute("gs-y")

    const template = document.createElement("template")
    template.dataset.dashboardGridTarget = "template"
    template.dataset.widgetKey = key
    template.content.appendChild(clone)

    const button = document.createElement("button")
    button.type = "button"
    button.className = "block w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
    button.dataset.action = "click->dashboard-grid#addWidget"
    button.dataset.dashboardGridWidgetKeyParam = key
    button.textContent = title

    this.addWidgetMenuTarget.append(button, template)
  }
}
